import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { RefreshCw, TrendingUp, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Traffic & Attribution admin panel.
 *
 * Answers "how many clicks / signups did Reddit (or LinkedIn, or newsletters,
 * or …) actually deliver?" — sliced by utm_source over a configurable window.
 * Data is captured server-side by POST /api/attribution/pageview and stamped
 * on signup rows via the `attribution` sub-document.
 */
export default function AdminAttributionTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/attribution/summary?days=${days}`, { headers: getAuthHeaders() })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const sources = data?.by_source || [];
  const totalClicks = data?.totals?.clicks || 0;

  // Roll up brand / exec / paying counts into a per-source lookup so we can
  // render a single funnel table.
  const brandBySrc = Object.fromEntries((data?.brand_signups_by_source || []).map(r => [r.utm_source, r.count]));
  const execBySrc = Object.fromEntries((data?.executive_signups_by_source || []).map(r => [r.utm_source, r.count]));
  const payingBySrc = Object.fromEntries((data?.paying_brands_by_source || []).map(r => [r.utm_source, r.count]));
  // Union all sources present in any of the four datasets so we don't drop a
  // channel that has signups but no click record (rare but possible).
  const allSources = Array.from(new Set([
    ...sources.map(s => s.utm_source),
    ...Object.keys(brandBySrc),
    ...Object.keys(execBySrc),
    ...Object.keys(payingBySrc),
  ].filter(Boolean)));

  const rowFor = (src) => {
    const pv = sources.find(s => s.utm_source === src);
    return {
      utm_source: src,
      clicks: pv?.clicks || 0,
      unique_visitors: pv?.unique_visitors || 0,
      brand_signups: brandBySrc[src] || 0,
      exec_signups: execBySrc[src] || 0,
      paying_brands: payingBySrc[src] || 0,
    };
  };
  const rows = allSources.map(rowFor).sort((a, b) => b.clicks - a.clicks || b.brand_signups - a.brand_signups);

  return (
    <div data-testid="admin-attribution" className="space-y-6">
      {/* Header + controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#FF4343]" /> Traffic &amp; Attribution</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">Clicks → signups → paying brands, broken down by campaign source. Add <code className="text-[#FF4343]">?utm_source=reddit&amp;utm_medium=paid&amp;utm_campaign=launch</code> to your ad links.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))}
            data-testid="attribution-range"
            className="bg-[#111827] border border-white/10 text-white text-xs px-3 py-2 focus:outline-none focus:border-[#FF4343]">
            <option value={1}>Last 24 hrs</option>
            <option value={4}>Last 4 days</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button onClick={load} data-testid="attribution-refresh"
            className="text-xs px-3 py-2 border border-white/20 text-white hover:bg-white/5 flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total clicks', value: data.totals.clicks, color: 'text-white' },
            { label: 'Brand signups', value: data.totals.brand_signups, color: 'text-[#FF4343]' },
            { label: 'Executive signups', value: data.totals.executive_signups, color: 'text-white' },
            { label: 'Paying brands', value: data.totals.paying_brands, color: 'text-[#10B981]' },
          ].map(s => (
            <div key={s.label} className="bg-[#111827] border border-white/10 p-4">
              <span className={`block font-mono text-3xl font-bold tabular-nums leading-tight ${s.color}`}>{s.value.toLocaleString()}</span>
              <p className="text-[10px] font-mono uppercase text-[#6B7280] mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Funnel by source */}
      <div className="bg-[#111827] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="attribution-source-table">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              <th className="text-left px-4 py-3 text-[10px] font-mono uppercase text-[#6B7280]">Source</th>
              <th className="text-right px-4 py-3 text-[10px] font-mono uppercase text-[#6B7280]">Clicks</th>
              <th className="text-right px-4 py-3 text-[10px] font-mono uppercase text-[#6B7280]">Unique visitors</th>
              <th className="text-right px-4 py-3 text-[10px] font-mono uppercase text-[#6B7280]">Brand signups</th>
              <th className="text-right px-4 py-3 text-[10px] font-mono uppercase text-[#6B7280]">Exec signups</th>
              <th className="text-right px-4 py-3 text-[10px] font-mono uppercase text-[#6B7280]">Paying</th>
              <th className="text-right px-4 py-3 text-[10px] font-mono uppercase text-[#6B7280]">Click → Signup</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-[#9CA3AF]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-[#9CA3AF]">No traffic captured yet for this window. Make sure your ad URLs include <code className="text-[#FF4343]">?utm_source=…</code>.</td></tr>
            ) : rows.map(r => {
              const totalSignups = r.brand_signups + r.exec_signups;
              const conv = r.clicks > 0 ? ((totalSignups / r.clicks) * 100).toFixed(2) : '—';
              return (
                <tr key={r.utm_source} className="border-b border-white/5 hover:bg-white/5" data-testid={`attribution-row-${r.utm_source}`}>
                  <td className="px-4 py-3 font-semibold text-white">
                    <span className={`inline-block text-[10px] font-mono uppercase px-2 py-0.5 mr-2 ${
                      r.utm_source === 'reddit' ? 'bg-orange-500/15 text-orange-400 border border-orange-500/30' :
                      r.utm_source === 'linkedin' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' :
                      r.utm_source === 'twitter' ? 'bg-sky-500/15 text-sky-400 border border-sky-500/30' :
                      r.utm_source === 'direct' ? 'bg-white/10 text-[#9CA3AF] border border-white/10' :
                      'bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/30'
                    }`}>{r.utm_source}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-white">{r.clicks.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#9CA3AF]">{r.unique_visitors.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#FF4343]">{r.brand_signups}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#9CA3AF]">{r.exec_signups}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#10B981]">{r.paying_brands}</td>
                  <td className="px-4 py-3 text-right font-mono text-white">{typeof conv === 'string' ? conv : `${conv}%`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recent visits live tail */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">Latest 20 visits</h3>
        <div className="bg-[#111827] border border-white/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase text-[#6B7280]">When</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase text-[#6B7280]">Path</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase text-[#6B7280]">Source</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase text-[#6B7280]">Campaign</th>
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase text-[#6B7280]">Referrer</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_visits || []).slice(0, 20).map((v, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="px-3 py-2 font-mono text-[#9CA3AF] whitespace-nowrap">{new Date(v.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="px-3 py-2 font-mono text-white truncate max-w-[180px]">{v.path}</td>
                  <td className="px-3 py-2 font-mono text-[#FF4343]">{v.utm_source || 'direct'}{v.utm_medium && v.utm_medium !== 'unknown' ? ` / ${v.utm_medium}` : ''}</td>
                  <td className="px-3 py-2 font-mono text-[#9CA3AF] truncate max-w-[140px]">{v.utm_campaign || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[#6B7280] truncate max-w-[220px]">{v.referrer || '—'}</td>
                </tr>
              ))}
              {(!data?.recent_visits || data.recent_visits.length === 0) && (
                <tr><td colSpan={5} className="text-center py-6 text-[#9CA3AF]">No visits captured yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* UTM URL builder cheat sheet */}
      <div className="bg-[#111827] border border-[#FF4343]/30 border-l-[3px] border-l-[#FF4343] p-5">
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><ExternalLink className="w-4 h-4 text-[#FF4343]" /> UTM URL cheat sheet</h3>
        <p className="text-xs text-[#9CA3AF] mb-3">Paste these into the destination URL on Reddit, LinkedIn, X, or any newsletter you sponsor. Anything after <code className="text-[#FF4343]">?</code> is the attribution key.</p>
        <div className="space-y-2 font-mono text-[11px]">
          {[
            ['Reddit ad',        'https://freddyleads.com/?utm_source=reddit&utm_medium=paid&utm_campaign=launch_q1'],
            ['LinkedIn ad',      'https://freddyleads.com/?utm_source=linkedin&utm_medium=paid&utm_campaign=launch_q1'],
            ['X (Twitter) ad',   'https://freddyleads.com/?utm_source=twitter&utm_medium=paid&utm_campaign=launch_q1'],
            ['Newsletter (Beehiiv)', 'https://freddyleads.com/?utm_source=beehiiv&utm_medium=newsletter&utm_campaign=founders_edition'],
            ['Cold email',       'https://freddyleads.com/?utm_source=email&utm_medium=cold&utm_campaign=outbound_q1'],
            ['Podcast mention',  'https://freddyleads.com/?utm_source=podcast&utm_medium=sponsorship&utm_campaign=sales_show_ep32'],
          ].map(([label, url]) => (
            <div key={label} className="flex items-center gap-2 bg-white/5 px-3 py-2 border border-white/10">
              <span className="text-[10px] uppercase text-[#6B7280] w-32 flex-shrink-0">{label}</span>
              <code className="text-[#9CA3AF] break-all flex-1">{url}</code>
              <button
                onClick={() => navigator.clipboard?.writeText(url)}
                data-testid={`copy-utm-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="text-[10px] px-2 py-1 border border-white/20 text-white hover:bg-white/5 flex-shrink-0">Copy</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
